import React, { useEffect, useState } from 'react';
import { fetchDeliverableTranscript } from '../../utils/transcriptStore';
import { timecodeAt, type Cue } from '../../utils/transcriptCues';
interface VideoTranscriptProps {
  projectSlug: string;
  /**
   * The deliverable the transcript is stored against — the video's own key
   * when the transcript rides on the film, or the separate Transcript
   * deliverable's when there is one.
   */
  deliverableKey: string;
  /** What the page already holds, shown at once and kept if no timings exist. */
  fallback: string;
  /**
   * Given a timecode when the reader clicks a line. Omitted when the film is
   * settled or the reader may not ask for changes, and the transcript then
   * simply reads rather than clicks.
   */
  onUseTimecode?: (label: string) => void;
  /** The container's own classes — each placement frames it differently. */
  className?: string;
  /** Beside the film, in a third of the width, rather than under it. */
  compact?: boolean;
}
interface Line {
  label: string;
  text: string;
}

/**
 * A film's transcript, for reference, with the time each passage was said.
 *
 * This is the half of audit §3.3 that could be built. The other half — click
 * a line and the video jumps there — cannot: the player beside it is a
 * cross-origin Google Drive iframe with no API that ignores a start time in
 * the URL (four spellings tried, all opened on the same frame). Seeking would
 * mean not hosting review copies on Drive, which is Tom's decision to make,
 * not something to work around here.
 *
 * So the click does the next most useful thing, and the thing that fills a
 * real hole: it puts the timecode on the change request. Until now the
 * client page took a change as prose alone — `requestChanges` was called
 * with no timecode at all — so the studio's own list of changes showed "—"
 * in that column for every single one.
 *
 * THE WHOLE LINE IS THE TARGET, not the timecode (30 Sep 2026). It was the
 * stamp alone — a grey four-character number in an 11-unit column — while
 * the box underneath said "Click a line in the transcript". Tom did exactly
 * that, on the words, and nothing happened. If the instruction says line,
 * the line is the button.
 *
 * TIMINGS COME FROM THE STORED CUES, NOT FROM THE PROSE. A transcript whose
 * timecodes are only characters inside the text — "[00:00:00] JACOB: …" in
 * an uploaded document — has no cues, so this renders it as the plain block
 * and nothing is clickable. That is what Bloomberg Ep3 looked like on 29
 * Sep, and the fix was at the other end: `public.parse_timecoded_text`
 * reads those markers on upload and stores real cues, exactly as the SRT
 * path does. Nothing here needs to know which kind of transcript it got.
 *
 * PRESENTATION IS THE CALLER'S. The two places a transcript appears frame it
 * quite differently — a bordered block under the player, a plain scrolling
 * column beside it — so this renders the rows and takes the container's
 * classes from whoever is placing it. What it will not do is vary the
 * timecode column: that stays one width and one alignment everywhere,
 * because a column of times that does not line up looks broken rather than
 * styled.
 *
 * Read directly rather than through PortalContext on purpose. The timings
 * live on the transcript revision, the context carries only the text, and
 * threading a new field through a 2,600-line file to save one query on a
 * page that already makes several is a bad trade.
 */
export function VideoTranscript({
  projectSlug,
  deliverableKey,
  fallback,
  onUseTimecode,
  className = '',
  compact = false
}: VideoTranscriptProps) {
  const [lines, setLines] = useState<Line[] | null>(null);
  useEffect(() => {
    if (!projectSlug || !deliverableKey) return;
    let live = true;
    setLines(null);
    void fetchDeliverableTranscript(projectSlug, deliverableKey).then(result => {
      if (!live || result.status !== 'ok') return;
      const stored = result.transcript;
      if (!stored?.cues?.length || !stored.cueText) return;
      setLines(split(stored.cueText, stored.cues));
    });
    return () => {
      live = false;
    };
  }, [projectSlug, deliverableKey]);
  const body = compact ? 'text-[0.95rem] leading-relaxed text-charcoal' : 'font-display text-lg leading-relaxed text-charcoal';

  /*
   * `self-start` is doing real work. A stamp is a flex child of the row, so
   * without it the row stretches it to the full height of the paragraph and
   * a <button> centres its own label inside that — which put "1:24" beside
   * the THIRD line of a four-line paragraph, pointing at a sentence that
   * was said twenty seconds after the time shown. It has to sit against the
   * first line, because the first line is what it is the time of.
   */
  const stamp = `label-sm w-11 shrink-0 self-start tabular-nums text-mute ${compact ? 'pt-0.5' : 'pt-1'}`;
  const gap = compact ? 'mt-5' : 'mt-6';

  /* Until the timings arrive — and for ever, on a transcript that has none —
     this reads exactly as it did before any of this was built. */
  if (!lines?.length) {
    return <p className={`m-0 whitespace-pre-line ${body} ${className}`} data-id="element-252" data-mp="vi4j7pi986qh">{fallback}</p>;
  }
  return <div className={className} data-id="element-253" data-mp="qj1r15ndhuzx">
      {lines.map((line, index) => {
      const spacing = index > 0 ? gap : '';
      /* A heading with no time, and any row on a settled film, is read
         rather than pressed. */
      if (!line.label || !onUseTimecode) {
        return <div key={`${line.label}-${index}`} className={`flex gap-4 ${spacing}`} data-id="element-254" data-mp="1e9faw8vt0njf">
              <span className={stamp} aria-hidden={!line.label} data-id="element-255" data-mp="19a2k5d1tawbk">
                {line.label}
              </span>
              <p className={`m-0 ${body}`} data-id="element-256" data-mp="14bhl3gyaqiop">{line.text}</p>
            </div>;
      }
      return <button key={`${line.label}-${index}`} type="button" onClick={() => onUseTimecode(line.label)} aria-label={`Ask for a change at ${line.label}`} className={`group flex w-full gap-4 text-left transition-colors duration-150 ease-out hover:bg-paperDeep ${spacing}`} data-id="element-257" data-mp="3u0jvd33xsjvl" data-mpi={index}>
            <span className={`${stamp} transition-colors duration-150 ease-out group-hover:text-ink`} data-id="element-258" data-mp="2l18z5i4vhpxm">
              {line.label}
            </span>
            <p className={`m-0 ${body}`} data-id="element-259" data-mp="2c1voim50ivbb">{line.text}</p>
          </button>;
    })}
    </div>;
}

/**
 * The transcript as paragraphs, each with the time its first cue began.
 *
 * Paragraph by paragraph rather than cue by cue: the database already breaks
 * the prose where the speech breaks (a gap of three seconds or a change of
 * speaker), so these are the natural units. One row per subtitle would be
 * thirty-five two-line fragments for a three-minute film — accurate, and
 * unreadable.
 *
 * Offsets are found by scanning forward rather than by assuming how long the
 * separator between two paragraphs is, so a transcript written with single
 * newlines lands in the right place as surely as one written with double.
 */
function split(text: string, cues: Cue[]): Line[] {
  const lines: Line[] = [];
  let cursor = 0;
  for (const piece of text.split(/\n{2,}/)) {
    const body = piece.trim();
    if (!body) continue;
    const at = text.indexOf(body, cursor);
    if (at === -1) continue;
    cursor = at + body.length;
    lines.push({
      label: timecodeAt(cues, at),
      text: body
    });
  }

  /**
   * A HOLE IN THE MIDDLE of the column reads as a fault rather than as
   * "unknown", so a transcript with one falls back to the plain block.
   *
   * A HEADING AT THE TOP is different, and treating the two the same threw
   * away every timecode in an uploaded transcript that began "Episode 3 -
   * EDIT 1 - TRANSCRIPT" (29 Sep 2026). That line genuinely is not at a
   * time; it sits with an empty stamp, and the rule starts at the first
   * paragraph that has one.
   */
  const firstTimed = lines.findIndex(line => Boolean(line.label));
  if (firstTimed === -1) return [];
  return lines.slice(firstTimed).every(line => Boolean(line.label)) ? lines : [];
}